<template>
  <div class="d-flex flex-column align-items-center page-wrapper">
    <img :src="require(`../brand/${this.brandConfig.assetBrand || this.brandConfig.brand}/assets/evina-img.webp`)" class="w-100">
    <div class="header-text mt-3">
      Success!
    </div>
    <div class="sub-header-text mt-1">
      Thank you for joining {{ this.brandConfig.tabTitle }}!
    </div>
    <div v-if="pageLoading" class="mt-4">
      <span class="spinner"></span>
    </div>
    <div v-if="crossSellService && !pageLoading" class="d-flex flex-column upsell-container col-11 pb-2">
      <img :src="crossSellService.crossSellBannerUrl || require(`../brand/${crossSellService.assetBrand || crossSellService.brand}/assets/cross-sell-banner.png`)" class="h-100">
      <div class="d-flex flex-column justify-content-center">
        <div class="d-flex mt-3">
          <span class="header-text mx-2">{{ crossSellService.title }}</span>
          <div class="free-text-container ms-auto d-flex justify-content-center align-items-center free-text mx-2">
            Free for 24 Hours
          </div>
        </div>
        <div class="d-flex justify-content-center sub-header-text mt-1">
          {{ crossSellService.bodyText }}
        </div>
      </div>
      <div class="d-flex justify-content-center sub-header-text">
        <button class="accept-btn" 
        :style="{ 
          backgroundColor: brandConfig.buttonColor || 'limegreen', 
          color: brandConfig.buttonTextColor || '#FFFFFF' 
        }" 
      :disabled="pageLoading" @click="joinNowClicked">JOIN NOW</button>
      </div>
      <div class="d-flex justify-content-center disclaimer-text mt-1">
        FREE for 24 hours, then {{ crossSellService.billingText }}. T&C's Apply.
      </div>
    </div>
    <span v-if="!pageLoading" class="sub-header-text underline mt-3" @click="onGoToPortalClicked">Go to {{
        this.brandConfig.tabTitle
      }}</span>
  </div>
</template>

<script>
import {mapState, mapMutations} from "vuex";
import {ref} from 'vue';
import {v4 as uuidv4} from 'uuid';
import {getEvinaAuthToken, requestEvinaPaymentUrl} from '@/services/evinaService';
import SafaricomService from "@/services/safaricomService";
import {crossSellConfig, getCrossSellPriorityOrder} from "@/config/crossSell";
import {triggerGTMEvent} from "@/functions/addGTM";
import {handleMFilterFlow} from "@/functions/mFilter";
import {msisdnLookup} from "@/utils/msisdnLookup";
import {showBrandDebugAlert} from "@/functions/brandDebug";

const isLoading = ref(false);
const errorMessage = ref(null);

// A real msisdn is all digits. Anything else — '', 'undefined', 'Undefined',
// 'null', or any other non-numeric junk from an upstream redirect (any casing) —
// is treated as absent, so we never emit ?msisdn=<garbage> on an outbound redirect.
function cleanMsisdn(value) {
  const trimmed = typeof value === 'string' ? value.trim() : value;
  return trimmed && /^\d+$/.test(trimmed) ? trimmed : null;
}

export default {
  computed: {
    ...mapState(['brandConfig', 'msisdn'])
  },
  data() {
    return {
      crossSellService: undefined,
      redirectId: undefined,
      pageLoading: true,
      resolvedMsisdn: null
    }
  },
  methods: {
    ...mapMutations(['setMsisdn']),
    getCrossSellTrackingDetails() {
      return {
        campaignId: this.crossSellService.campaignId ?? null,
        // Hyphen-less so the composed cross-sell transaction_id (`<clickId>-<campaignId>`) splits
        // unambiguously back into its two parts on the subscription callback.
        clickId: uuidv4().replace(/-/g, ''),
        publisherId: null,
        googleClickId: null,
      };
    },
    async handleEvinaCrossSellSubscription() {
      const trackingDetails = this.getCrossSellTrackingDetails();
      const token = await getEvinaAuthToken();
      if (!token) {
        this.$router.push({name: 'Error'});
        return;
      }

      await SafaricomService.queueUserSubscription(
          this.resolvedMsisdn,
          this.crossSellService.offerCode,
          this.crossSellService.productId,
          trackingDetails.campaignId,
          'Web',
          trackingDetails.clickId,
          trackingDetails.publisherId,
          null,
          trackingDetails.googleClickId
      );

      const paymentResponse = await requestEvinaPaymentUrl(
          token,
          this.crossSellService.pricePoint,
          this.crossSellService.offerCode,
          this.resolvedMsisdn,
          `${this.crossSellService.gameUrl}?msisdn=${this.resolvedMsisdn}`,
          this.crossSellService.errorUrl
      );

      if (paymentResponse && paymentResponse.url) {
        console.log("Redirecting to Evina consent page:", paymentResponse.url);
        window.location.href = paymentResponse.url;
      } else {
        this.$router.push({name: 'Error'});
      }
    },
    async handleMFilterCrossSellSubscription() {
      const trackingDetails = this.getCrossSellTrackingDetails();
      // No inbound transaction_id on the thank-you page, so compose one that round-trips attribution
      // through the consent gateway: `<clickId>-<campaignId>` (publisherId is not applicable to cross-sell).
      const transactionId = trackingDetails.campaignId
          ? `${trackingDetails.clickId}-${trackingDetails.campaignId}`
          : trackingDetails.clickId;
      await handleMFilterFlow(this.crossSellService, this.$router, {
        msisdn: this.resolvedMsisdn,
        transactionId,
      });
    },
    async joinNowClicked() {
      triggerGTMEvent('cross_sell_subscribe_button_clicked');
      clearTimeout(this.redirectId);
      isLoading.value = true;
      errorMessage.value = null;

      try {
        if (this.resolvedMsisdn) {
          if (this.crossSellService.flowType === 'mfilter') {
            await this.handleMFilterCrossSellSubscription();
          } else {
            await this.handleEvinaCrossSellSubscription();
          }
        } else {
          alert(`Please turn off Wifi and refresh the page.`);
        }
      } catch (error) {
        errorMessage.value = error.response?.data?.message || error.message || "An unknown error occurred.";
        this.$router.push({name: 'Error'});
      } finally {
        // We might not see this if the redirect is successful
        isLoading.value = false;
      }
    },
    async setCrossSellConfig(msisdn) {
      const currentServiceFamily = this.brandConfig.serviceFamily;
      const currentSourceGroup = this.brandConfig.sourceGroup;
      const priorityOrder = getCrossSellPriorityOrder(currentSourceGroup);
      const filteredCrossSellConfig = crossSellConfig
          .filter(crossSellService => crossSellService.serviceFamily !== currentServiceFamily)
          .sort((left, right) => priorityOrder.indexOf(left.serviceFamily) - priorityOrder.indexOf(right.serviceFamily));

      const inactiveServices = [];

      for (const crossSellService of filteredCrossSellConfig) {
        // TODO: Replace the per-service lookup loop with a single query if the backend exposes one.
        const userResponse = (await SafaricomService.getSafaricomUser(msisdn, crossSellService.productId)).data;
        if (userResponse?.Status?.toLowerCase() !== 'active') inactiveServices.push(crossSellService);
      }

      if (inactiveServices.length > 0) this.crossSellService = inactiveServices[0];
    },
    goToPortal() {
      // Without a real msisdn, send the user to the platform bare — its own
      // enrichment/landing flow takes over. Never emit ?msisdn=undefined.
      window.location.href = this.resolvedMsisdn
          ? `${this.brandConfig.platformUrl}?msisdn=${this.resolvedMsisdn}`
          : this.brandConfig.platformUrl;
    },
    onGoToPortalClicked() {
      clearTimeout(this.redirectId);
      this.goToPortal();
    }
  },
  async mounted() {
    try {
      triggerGTMEvent('thank_you_page_view');
      const urlParams = new URLSearchParams(window.location.search);
      const msisdnFromUrl = cleanMsisdn(urlParams.get('msisdn'));
      this.resolvedMsisdn = msisdnFromUrl || cleanMsisdn(this.msisdn);
      let msisdnSource = msisdnFromUrl ? 'url param' : (this.resolvedMsisdn ? 'store' : 'none');
      if (!this.resolvedMsisdn) {
        // The DSDP consent gateway redirect doesn't carry the msisdn and the store doesn't
        // survive the round-trip, so fall back to header enrichment (silent: this is a
        // best-effort background lookup — no alert popups on page load). Only works on
        // Safaricom mobile data; on WiFi the cross-sell simply stays hidden.
        const lookupResponse = await msisdnLookup({silent: true});
        this.resolvedMsisdn = cleanMsisdn(lookupResponse?.msisdn);
        if (this.resolvedMsisdn) msisdnSource = 'header enrichment';
      }
      if (this.resolvedMsisdn && !this.$store.state.msisdn) {
        this.setMsisdn(this.resolvedMsisdn);
      }
      if (this.resolvedMsisdn) {
        // Blocking debug alert (shown only when the brand debug flag is on): the cross-sell
        // load and the portal redirect below run only after the tester dismisses it.
        showBrandDebugAlert(
            this.brandConfig,
            'ThankYou MSISDN retrieved\n\n' +
            'msisdn: ' + this.resolvedMsisdn + '\n' +
            'source: ' + msisdnSource + '\n' +
            'redirecting to cross-sell/portal flow after close'
        );
        await this.setCrossSellConfig(this.resolvedMsisdn);
        this.redirectId = setTimeout(() => this.goToPortal(), 6000);
      } else {
        showBrandDebugAlert(this.brandConfig, 'ThankYou MSISDN unavailable (url/store/header enrichment all missed) - cross-sell hidden');
      }
    } finally {
      this.pageLoading = false;
    }
  }
}
</script>

<style scoped>
.page-wrapper {
  height: 100vh;
}

.header-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
}

.sub-header-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 1rem;
}

.free-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.9rem;
}

.disclaimer-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.7rem;
}

.terms-and-conditions {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.7rem;
  text-decoration: underline;
}

.accept-btn {
  background-color: limegreen;
  color: #FFFFFF;
  border-radius: 6px;
}

.decline-btn {
  background-color: #424242;
  color: #FFFFFF;
}

h1 {
  margin-bottom: 0.5rem;
  color: #333;
}

p {
  color: #666;
  line-height: 1.6;
}

.upsell-container {
  border-radius: 3px;
  background-color: rgba(0, 128, 0, 0.4);
}

button {
  padding: 15px 30px;
  font-size: 1.2rem;
  cursor: pointer;
  margin-top: 0.8rem;
  transition: background-color 0.3s, transform 0.2s;
  width: 98%;
  border: none;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.error-message {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 8px;
  text-align: left;
}

.error-message p {
  margin: 0;
  color: #c62828;
}

.free-text-container {
  background-color: #c62828;
  border-radius: 8px;
  padding: 0 0.5rem 0 0.5rem;
}

.footer {
  margin-top: 2rem;
  font-size: 0.8rem;
  color: #999;
}

.underline {
  text-decoration: underline;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
